'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { Play, Volume2, Upload, Loader2, CheckCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

const PROMO_VIDEO_URL =
  'https://pkvxlrsjkgqgqymukkav.supabase.co/storage/v1/object/public/Promo%20Video/Messenger_creation_DD574811-DB43-4A92-8D4B-B519BF5DC3F4.mp4';

export default function HeroSection() {
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Check if current user is authenticated (admin upload capability)
    const checkAuth = async () => {
      try {
        const supabase = createClient();
        const { data: { user } } = await supabase.auth.getUser();
        setIsAdmin(!!user);
      } catch {
        setIsAdmin(false);
      }
    };
    checkAuth();
  }, []);

  const toggleVideo = () => {
    if (!videoRef?.current) return;
    if (videoPlaying) {
      videoRef?.current?.pause();
    } else {
      videoRef?.current?.play();
    }
    setVideoPlaying(!videoPlaying);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      setUploadError('Please select a valid video file (MP4, WebM, MOV).');
      return;
    }

    if (file.size > 52428800) {
      setUploadError('File size must be under 50MB.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      const supabase = createClient();
      const { error } = await supabase.storage
        .from('promo-videos')
        .upload('promo-reel.mp4', file, { upsert: true, cacheControl: '3600' });

      if (error) {
        setUploadError(error.message);
      } else {
        setUploadSuccess(true);
        // Reset video player state
        setVideoPlaying(false);
        if (videoRef.current) {
          videoRef.current.load();
        }
        setTimeout(() => setUploadSuccess(false), 4000);
      }
    } catch (err: any) {
      setUploadError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center pt-16 pb-12 overflow-hidden">
      {/* Promotional poster background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/assets/images/image-1-1785357199333.webp')" }}
      />
      {/* Dark gradient overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/90" />
      {/* Subtle vignette */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.7) 100%)' }} />

      {/* Animated background beams */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-px h-full opacity-10" style={{ background: 'linear-gradient(180deg, transparent, var(--neon-gold), transparent)' }} />
        <div className="absolute top-0 left-3/4 w-px h-full opacity-10" style={{ background: 'linear-gradient(180deg, transparent, var(--neon-cyan), transparent)' }} />
        <div className="absolute top-0 left-1/2 w-px h-full opacity-20" style={{ background: 'linear-gradient(180deg, transparent, var(--neon-gold), transparent)' }} />
      </div>

      <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10 w-full">
        <div className="flex flex-col items-center text-center gap-8">

          {/* Maximum Effort Logo with spinning gold ring overlay */}
          <div className="relative w-52 h-52 flex items-center justify-center float-animation">
            {/* Spinning gold ring overlay — simulates the logo's gold ring spinning */}
            <div
              className="absolute inset-0 rounded-full pointer-events-none spinning-ring"
              style={{
                border: '3px solid transparent',
                borderTopColor: 'var(--neon-gold)',
                borderRightColor: 'var(--neon-gold)',
                borderBottomColor: 'rgba(255,215,0,0.15)',
                borderLeftColor: 'rgba(255,215,0,0.15)',
                boxShadow: '0 0 18px rgba(255,215,0,0.55), inset 0 0 10px rgba(255,215,0,0.1)',
                borderRadius: '50%',
              }}
            />
            {/* Secondary counter-spinning ring for depth */}
            <div
              className="absolute inset-3 rounded-full pointer-events-none spinning-ring-slow"
              style={{
                animationDirection: 'reverse',
                border: '2px solid transparent',
                borderTopColor: 'rgba(255,215,0,0.6)',
                borderRightColor: 'rgba(255,215,0,0.15)',
                borderBottomColor: 'rgba(255,215,0,0.6)',
                borderLeftColor: 'rgba(255,215,0,0.15)',
                borderRadius: '50%',
              }}
            />

            {/* Maximum Effort logo image */}
            <div className="relative w-44 h-44 rounded-full overflow-hidden" aria-label="Maximum Effort DJ logo">
              <img
                src="/assets/images/760105518_1676653893415634_6565105660036259608_n-1-1785527968606.webp"
                alt="Maximum Effort DJ ExXtra logo"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Brand headline */}
          <div className="fade-in-up">
            <p className="text-xs font-mono tracking-[0.4em] text-muted-foreground uppercase mb-2">
              Northwest Indiana&apos;s Premier
            </p>
            <h1 className="text-5xl md:text-7xl font-extrabold leading-none mb-3">
              <span className="gold-gradient-text text-glow-gold">Maximum</span>
              <br />
              <span className="text-foreground">Effort</span>
            </h1>
            <p className="text-xl md:text-2xl font-semibold text-muted-foreground">
              Mobile DJ &amp; Karaoke Services
            </p>
            <p className="mt-2 text-sm font-mono text-accent text-glow-cyan tracking-widest">
              DJ E<span className="text-lg font-black">X</span>Xtra — Christopher Harris
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mt-2">
            <Link
              href="/sign-up-login-screen?role=client"
              className="px-8 py-3 rounded-xl text-base font-bold gold-gradient text-black hover:opacity-90 active:scale-95 transition-all duration-150 glow-gold"
            >
              Book Your Event
            </Link>
            <Link
              href="/sign-up-login-screen?role=client"
              className="px-8 py-3 rounded-xl text-base font-bold border border-primary/50 text-primary hover:bg-primary/10 active:scale-95 transition-all duration-150"
            >
              Client Portal Sign-In
            </Link>
            <Link
              href="/sign-up-login-screen?role=vendor"
              className="px-8 py-3 rounded-xl text-base font-bold border border-accent/50 text-accent hover:bg-accent/10 active:scale-95 transition-all duration-150"
            >
              Vendor Portal Sign-In
            </Link>
          </div>

          {/* Promo Video Player */}
          <div className="w-full max-w-3xl mt-4 rounded-2xl overflow-hidden neon-border-gold relative group">
            <div className="relative bg-black aspect-video flex items-center justify-center">
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                poster="/assets/images/image-1-1785357199333.webp"
                onPlay={() => setVideoPlaying(true)}
                onPause={() => setVideoPlaying(false)}
                controls={videoPlaying}
              >
                <source src={PROMO_VIDEO_URL} type="video/mp4" />
              </video>

              {!videoPlaying && (
                <button
                  onClick={toggleVideo}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/20 transition-colors group"
                  aria-label="Play promo video"
                >
                  <div className="w-20 h-20 rounded-full gold-gradient flex items-center justify-center glow-gold group-hover:scale-110 transition-transform duration-200">
                    <Play size={32} className="text-black ml-1" />
                  </div>
                </button>
              )}
            </div>
            <div className="bg-card/80 px-4 py-2 flex items-center justify-between border-t border-border">
              <div className="flex items-center gap-2">
                <Volume2 size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground font-mono">Official Promo Reel — Weddings · Festivals · Karaoke · Bingo</span>
              </div>

              {/* Upload button — visible to authenticated users */}
              {isAdmin && (
                <div className="flex items-center gap-2">
                  {uploadSuccess && (
                    <span className="flex items-center gap-1 text-xs text-green-400 font-mono">
                      <CheckCircle size={12} /> Uploaded!
                    </span>
                  )}
                  {uploadError && (
                    <span className="text-xs text-red-400 font-mono max-w-[160px] truncate" title={uploadError}>
                      {uploadError}
                    </span>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime"
                    className="hidden"
                    onChange={handleUpload}
                    aria-label="Upload promo video"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold border border-primary/40 text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                    title="Upload MP4 promo reel to Supabase Storage"
                  >
                    {uploading ? (
                      <><Loader2 size={12} className="animate-spin" /> Uploading…</>
                    ) : (
                      <><Upload size={12} /> Upload Video</>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Scrolling marquee of services */}
          <div className="w-full overflow-hidden mt-4">
            <div className="flex gap-8 marquee-track whitespace-nowrap">
              {[
                '🎤 Karaoke Nights', '💍 Weddings', '🎲 Bingo Nights', '🧠 Trivia Events',
                '🎉 Private Parties', '🏢 Corporate Events', '🎓 Graduations', '🍺 Bar Events',
                '🎤 Karaoke Nights', '💍 Weddings', '🎲 Bingo Nights', '🧠 Trivia Events',
                '🎉 Private Parties', '🏢 Corporate Events', '🎓 Graduations', '🍺 Bar Events',
              ]?.map((item, i) => (
                <span key={`marquee-${i}`} className="text-sm font-semibold text-muted-foreground">
                  {item}
                  <span className="ml-8 text-primary">✦</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}